import { useCallback, useRef, useState, useMemo } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useSaveNote } from './useSaveNote'
import { type NoteType } from '../utils/note'
import {
  parseTaskState,
  toggleTaskState,
  updateTaskLineState,
} from '../utils/task'
import { trackError } from '../lib/analytics'
import { useAuthenticatedUser } from '../providers/UserProvider'
import {
  cachedNotesContext,
  getNoteFromCache,
  getCurrentCacheVersion,
} from '../contexts/CachedNotes'
import { cacheKeyFromNoteType } from '../utils/queryKeyFactory'

type TaskToggleConfig = {
  recordName: string
  noteType: NoteType
  lineIndex: number
  expectedContent?: string // for robust line finding
  onSuccess?: () => void
  onError?: (error: Error) => void
}

type LineSearchResult =
  | {
      lineIndex: number
      content: string
    }
  | undefined

/**
 * Find a task line using multiple fallback strategies
 */
function findTaskLine(
  noteContent: string,
  originalLineIndex: number,
  expectedContent?: string
): LineSearchResult {
  if (!noteContent) return undefined

  const lines = noteContent.split('\n')

  // Strategy 1: Exact line index match
  if (
    lines[originalLineIndex] &&
    (!expectedContent || lines[originalLineIndex] === expectedContent)
  ) {
    return { lineIndex: originalLineIndex, content: lines[originalLineIndex] }
  }

  // Strategy 2: Content match for moved lines (if expectedContent provided)
  if (expectedContent) {
    const foundIndex = lines.indexOf(expectedContent)
    if (foundIndex !== -1) {
      return { lineIndex: foundIndex, content: lines[foundIndex] }
    }
  }

  // Strategy 3: Task content match (parse task and compare content)
  if (expectedContent) {
    const expectedParsed = parseTaskState(expectedContent)
    if (expectedParsed) {
      const foundIndex = lines.findIndex((line) => {
        const parsed = parseTaskState(line)
        return parsed && parsed.content === expectedParsed.content
      })
      if (foundIndex !== -1) {
        return { lineIndex: foundIndex, content: lines[foundIndex] }
      }
    }
  }

  return undefined
}

/**
 * Hook for updating task lines with intelligent line finding and robust edge case handling.
 * This is the single source of truth for all task toggling operations in the app.
 */
export function useTaskToggle(config: TaskToggleConfig) {
  const [isProcessing, setIsProcessing] = useState(false)
  const [saveError, setSaveError] = useState<Error | undefined>(undefined)
  const processingReference = useRef(false)

  const cachedNotesQueryClient = useQueryClient({ context: cachedNotesContext })
  const user = useAuthenticatedUser()

  // Get cache version to trigger re-computation when cache changes
  const cacheKey = useMemo(
    () => cacheKeyFromNoteType(config.noteType, user),
    [config.noteType, user]
  )

  // Get cache version to trigger re-computation when cache changes
  const cacheVersion = getCurrentCacheVersion(cachedNotesQueryClient, cacheKey)

  // Re-compute note when cache version changes
  const freshNote = useMemo(() => {
    if (!config.recordName) return

    return getNoteFromCache(cachedNotesQueryClient, cacheKey, config.recordName)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.recordName, cachedNotesQueryClient, cacheKey, cacheVersion])

  // Save note mutation
  const saveMutate = useSaveNote(() => {
    setIsProcessing(false)
    processingReference.current = false
    setSaveError(undefined)
    config.onSuccess?.()
  })

  // Handle task click
  const handleTaskClick = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault()
      event.stopPropagation()

      if (processingReference.current || !freshNote?.content) {
        return // Prevent multiple clicks while processing
      }

      setIsProcessing(true)
      processingReference.current = true

      // Find the task line using robust strategies
      const lineResult = findTaskLine(
        freshNote.content,
        config.lineIndex,
        config.expectedContent
      )

      if (!lineResult) {
        const error = new Error('Task line not found')
        trackError('useTaskToggle', 'Task line not found', {
          noteType: config.noteType,
          recordName: config.recordName,
          lineIndex: config.lineIndex,
          expectedContent: config.expectedContent,
        })
        setIsProcessing(false)
        processingReference.current = false
        setSaveError(error)
        config.onError?.(error)
        return
      }

      // Parse current task state and toggle it
      const parsed = parseTaskState(lineResult.content)
      if (!parsed) {
        const error = new Error('Invalid task format')
        trackError('useTaskToggle', 'Invalid task format', {
          noteType: config.noteType,
          recordName: config.recordName,
          lineContent: lineResult.content,
        })
        setIsProcessing(false)
        processingReference.current = false
        setSaveError(error)
        config.onError?.(error)
        return
      }

      const newState = toggleTaskState(parsed.state)
      const newLine = updateTaskLineState(lineResult.content, newState)

      if (!newLine) {
        const error = new Error('Failed to update task line')
        trackError('useTaskToggle', 'Failed to update task line', {
          noteType: config.noteType,
          recordName: config.recordName,
          originalLine: lineResult.content,
          newState,
        })
        setIsProcessing(false)
        processingReference.current = false
        setSaveError(error)
        config.onError?.(error)
        return
      }

      // Prepare the updated content
      const contentArray = freshNote.content.split('\n')
      contentArray[lineResult.lineIndex] = newLine
      const newContent = contentArray.join('\n')

      // Save the note with the updated content
      saveMutate.mutate({
        filename: freshNote.filename,
        recordName: freshNote.recordName,
        content: newContent,
        noteType: freshNote.noteType,
        parent: freshNote.parent,
        modificationDate: new Date(),
      })
    },
    [freshNote, config, saveMutate]
  )

  // Handle save errors
  if (saveMutate.error && !saveError) {
    const error = saveMutate.error
    trackError('useTaskToggle', 'Failed to save note', {
      error: error instanceof Error ? error.message : String(error),
      noteType: config.noteType,
      recordName: config.recordName,
    })
    setSaveError(error)
    setIsProcessing(false)
    processingReference.current = false
    config.onError?.(error)
  }

  return {
    isProcessing,
    saveError,
    handleTaskClick,
    note: freshNote, // Expose fresh note for consumers that need it
  }
}
